// Deal drawer — right slide-in create/edit panel (HubSpot/Zoho pattern).
// Works from the board (pass `pipeline`) or a contact/company record page
// (pass `pipelines` plus prefill/lock props). All dropdowns are custom
// components — no native selects. Stage is a clickable chevron bar.
import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const OPP_CUR = { GBP: '\u00A3', USD: '$', EUR: '\u20AC', NGN: '\u20A6', CAD: 'CA$', AUD: 'A$' };
const OPP_PRIORITIES = ['Low', 'Medium', 'High'];
const OPP_TYPES = ['New business', 'Existing business', 'Renewal', 'Upsell'];
const OPP_SOURCES = ['Inbound', 'Outbound', 'Referral', 'Partner', 'Event', 'Existing client', 'Other'];

function initials(name) {
  if (!name) return '?';
  const p = String(name).trim().split(/\s+/);
  const a = (p[0] && p[0][0]) || '';
  const b = (p[1] && p[1][0]) || '';
  return ((a + b) || '?').toUpperCase();
}

function Dropdown({ value, options, onChange, placeholder, renderVal, renderItem }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const sel = options.find(o => o.value === value);
  return (
    <div className="crmdd-dd" ref={boxRef}>
      <button type="button" className={'crmdd-dd-btn' + (open ? ' crmdd-dd-btn--open' : '') + (sel ? '' : ' crmdd-dd-btn--ph')} onClick={() => setOpen(!open)}>
        <span className="crmdd-dd-val">{sel ? (renderVal ? renderVal(sel) : sel.label) : (placeholder || 'Select')}</span>
        <span className="crmdd-dd-car">{'\u25BE'}</span>
      </button>
      {open && (
        <div className="crmdd-dd-pop">
          {options.map(o => (
            <div key={String(o.value)} className={'crmdd-dd-item' + (o.value === value ? ' crmdd-dd-item--sel' : '')}
                 onClick={() => { onChange(o.value); setOpen(false); }}>
              {renderItem ? renderItem(o) : <span>{o.label}</span>}
              {o.value === value && <span className="crmdd-dd-check">{'\u2713'}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssocPicker({ label, items, labelKey, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const filtered = q
    ? items.filter(i => (i[labelKey] || '').toLowerCase().includes(q.toLowerCase())).slice(0, 40)
    : items.slice(0, 40);
  return (
    <div className="crmdd-dd" ref={boxRef} style={{ width: '100%' }}>
      <button type="button" className="crmdd-assoc-add" onClick={() => { setOpen(!open); setQ(''); }}>
        <span aria-hidden="true">{'\uFF0B'}</span><span>{label}</span>
      </button>
      {open && (
        <div className="crmdd-dd-pop">
          <input autoFocus className="crmdd-inp" style={{ height: 34, marginBottom: 6 }} placeholder="Search" value={q} onChange={e => setQ(e.target.value)} />
          {filtered.map(i => (
            <div key={i.id} className="crmdd-dd-item" onClick={() => { onPick(i); setOpen(false); }}>
              <span className="crmdd-assoc-av" style={{ width: 26, height: 26, fontSize: 10 }}>{initials(i[labelKey])}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i[labelKey]}</span>
            </div>
          ))}
          {!filtered.length && <div className="crmdd-dd-item" style={{ color: 'var(--crm-muted)' }}>No matches</div>}
        </div>
      )}
    </div>
  );
}

export function DealDrawer({
  opp, pipeline, pipelines, defaultStageId, companies, contacts,
  prefillCompanyId, prefillContactId, lockCompany, lockContact,
  onClose, onSaved, clientId
}) {
  const isEdit = !!opp;

  const pipeList = pipelines && pipelines.length ? pipelines : (pipeline ? [pipeline] : []);
  const initialPipe = opp
    ? (pipeList.find(p => p.id === opp.pipeline_id) || pipeList[0])
    : (pipeline || pipeList[0]);

  const [pipelineId, setPipelineId] = useState(initialPipe ? initialPipe.id : null);
  const activePipe = pipeList.find(p => p.id === pipelineId) || initialPipe || { stages: [] };
  const stages = activePipe.stages || [];

  const [name, setName] = useState(opp ? opp.name : '');
  const [value, setValue] = useState(opp ? String(parseFloat(opp.value)) : '');
  const [currency, setCurrency] = useState(opp ? (opp.currency || 'GBP') : 'GBP');
  const [stageId, setStageId] = useState(opp ? opp.stage_id : (defaultStageId || (stages[0] && stages[0].id)));
  const [companyId, setCompanyId] = useState(opp ? opp.company_id : (prefillCompanyId != null ? prefillCompanyId : null));
  const [contactId, setContactId] = useState(opp ? opp.contact_id : (prefillContactId != null ? prefillContactId : null));

  const [priority, setPriority] = useState(opp ? (opp.priority || '') : '');
  const [oppType, setOppType] = useState(opp ? (opp.opp_type || '') : '');
  const [source, setSource] = useState(opp ? (opp.source || '') : '');
  const [ownerName, setOwnerName] = useState(opp ? (opp.owner_name || '') : '');
  const [openDate, setOpenDate] = useState(opp && opp.open_date ? opp.open_date.slice(0, 10) : '');
  const [closeDate, setCloseDate] = useState(opp && opp.expected_close_date ? opp.expected_close_date.slice(0, 10) : '');
  const [description, setDescription] = useState(opp ? (opp.description || '') : '');
  const [nextStep, setNextStep] = useState(opp ? (opp.next_step || '') : '');
  const [notes, setNotes] = useState(opp ? (opp.notes || '') : '');
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState(null);

  const company = companies.find(c => c.id === companyId) || null;
  const contact = contacts.find(c => c.id === contactId) || null;
  const contactPool = companyId ? contacts.filter(c => c.company_id === companyId) : contacts;

  const stageIdx = stages.findIndex(s => s.id === parseInt(stageId));
  const selectedStage = stages[stageIdx];
  const weighted = (value !== '' && selectedStage && selectedStage.win_probability != null)
    ? (parseFloat(value) || 0) * (selectedStage.win_probability / 100)
    : null;

  const pickPipeline = (id) => {
    setPipelineId(id);
    const p = pipeList.find(x => x.id === id);
    const first = p && p.stages && p.stages[0];
    setStageId(first ? first.id : null);
  };
  const pickCompany = (c) => { setCompanyId(c.id); if (!lockContact) setContactId(null); };

  const buildBody = () => ({
    name: name.trim(),
    value: value === '' ? 0 : parseFloat(value),
    currency,
    stage_id: parseInt(stageId),
    pipeline_id: pipelineId,
    company_id: companyId,
    contact_id: contactId,
    priority: priority || null,
    opp_type: oppType || null,
    source: source || null,
    owner_name: ownerName.trim() || null,
    open_date: openDate || null,
    expected_close_date: closeDate || null,
    description: description.trim() || null,
    next_step: nextStep.trim() || null,
    notes: notes.trim() || null
  });

  const validate = () => {
    if (!name.trim()) { setErr('Give the deal a name'); return false; }
    if (!pipelineId) { setErr('Pick a pipeline'); return false; }
    if (!stageId) { setErr('Pick a stage'); return false; }
    setErr('');
    return true;
  };

  const save = useCallback(async (addAnother) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = buildBody();
      const r = isEdit
        ? await fetch(`${API_BASE}/opportunities/${opp.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        : await fetch(`${API_BASE}/clients/${clientId}/opportunities`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).error || 'Save failed');
      toast.success(isEdit ? 'Deal updated' : 'Deal created');
      if (addAnother && !isEdit) {
        setName(''); setValue(''); setNextStep(''); setDescription(''); setNotes('');
        if (!lockCompany) setCompanyId(prefillCompanyId != null ? prefillCompanyId : null);
        setErr('');
      } else {
        onSaved();
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  });

  const doDelete = async () => {
    try {
      const r = await fetch(`${API_BASE}/opportunities/${opp.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Deal deleted');
      onSaved();
    } catch (e) { toast.error(e.message); }
  };
  const del = () => setConfirm({ title: 'Delete deal', body: 'This deal will be permanently deleted. This cannot be undone.', confirmLabel: 'Delete', danger: true, onConfirm: doDelete });

  const dirty = name.trim() || value || description.trim() || nextStep.trim();
  const tryClose = useCallback(() => {
    if (dirty) { setConfirm({ title: 'Discard changes', body: 'You have unsaved changes. Close without saving?', confirmLabel: 'Discard', danger: true, onConfirm: onClose }); return; }
    onClose();
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') tryClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tryClose, save]);

  const curOptions = Object.keys(OPP_CUR).map(k => ({ value: k, label: `${k} (${OPP_CUR[k]})` }));
  const pipeOptions = pipeList.map(p => ({ value: p.id, label: p.name }));
  const priOptions = OPP_PRIORITIES.map(p => ({ value: p, label: p }));
  const typeOptions = OPP_TYPES.map(t => ({ value: t, label: t }));
  const srcOptions = OPP_SOURCES.map(s => ({ value: s, label: s }));

  return (
    <div className="crmdd-overlay" onMouseDown={e => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className="crmdd-panel">

        <div className="crmdd-h">
          <div className="crmdd-h-l">
            <span className="crmdd-h-ic">{'\u25C7'}</span>
            <span className="crmdd-h-t">{isEdit ? 'Edit deal' : 'New deal'}</span>
          </div>
          <div className="crmdd-h-r">
            <span className="crmdd-h-hint">Esc to close</span>
            <button className="crmdd-x" onClick={tryClose}>{'\u00D7'}</button>
          </div>
        </div>

        <div className="crmdd-body">
          <input className="crmdd-title" value={name} onChange={e => setName(e.target.value)} placeholder="Untitled deal" autoFocus />

          {stages.length > 0 && (
            <div className="crmdd-stages">
              {stages.map((s, i) => {
                const active = i <= stageIdx;
                const isCur = i === stageIdx;
                const cls = 'crmdd-seg'
                  + (i === 0 ? ' crmdd-seg--first' : '')
                  + (i === stages.length - 1 ? ' crmdd-seg--last' : '')
                  + (active ? '' : ' crmdd-seg--empty')
                  + (isCur ? ' crmdd-seg--cur' : '');
                return (
                  <div key={s.id} className={cls} title={s.name}
                       style={active ? { background: s.colour, color: '#fff' } : undefined}
                       onClick={() => setStageId(s.id)}>
                    {s.name}
                  </div>
                );
              })}
            </div>
          )}

          <div className="crmdd-field">
            <div className="crmdd-lbl-row">
              <label className="crmdd-lbl">Amount <span className="crmdd-req">*</span></label>
            </div>
            <div className="crmdd-amt">
              <Dropdown value={currency} options={curOptions} onChange={setCurrency}
                        renderVal={o => <span>{OPP_CUR[o.value]} {o.value}</span>} />
              <input className="crmdd-amt-in" type="number" min="0" step="100" value={value} onChange={e => setValue(e.target.value)} placeholder="0" />
              {weighted != null && <div className="crmdd-amt-w">{'\u2248'} {OPP_CUR[currency]}{Math.round(weighted).toLocaleString()} weighted</div>}
            </div>
          </div>

          <div className="crmdd-grid2">
            <div>
              <label className="crmdd-lbl">Pipeline</label>
              <Dropdown value={pipelineId} options={pipeOptions} onChange={pickPipeline} placeholder="Pipeline" />
            </div>
            <div>
              <label className="crmdd-lbl">Stage <span className="crmdd-req">*</span></label>
              <Dropdown value={stageId}
                        options={stages.map(s => ({ value: s.id, label: s.name, colour: s.colour, win: s.win_probability }))}
                        onChange={setStageId}
                        renderVal={o => <span><span className="crmdd-dd-dot" style={{ background: o.colour, display: 'inline-block', marginRight: 8, verticalAlign: 'middle' }} />{o.label}</span>}
                        renderItem={o => (<><span className="crmdd-dd-dot" style={{ background: o.colour }} /><span>{o.label}</span>{o.win != null && <span className="crmdd-dd-meta">{o.win}%</span>}</>)} />
            </div>
          </div>

          <div className="crmdd-sec-h">Associate with</div>
          <div className="crmdd-assoc">
            {company ? (
              <div className="crmdd-assoc-card">
                <span className="crmdd-assoc-av">{initials(company.name)}</span>
                <div className="crmdd-assoc-main">
                  <div className="crmdd-assoc-nm">{company.name}</div>
                  <div className="crmdd-assoc-sub">Company{company.sector ? ' \u00B7 ' + company.sector : ''}</div>
                </div>
                {!lockCompany && <button className="crmdd-assoc-x" onClick={() => { setCompanyId(null); setContactId(null); }}>{'\u00D7'}</button>}
              </div>
            ) : (!lockCompany && (
              <AssocPicker label="Add a company" items={companies} labelKey="name" onPick={pickCompany} />
            ))}

            {contact ? (
              <div className="crmdd-assoc-card">
                <span className="crmdd-assoc-av">{contact.photo_url ? <img src={contact.photo_url} alt="" /> : initials(contact.full_name)}</span>
                <div className="crmdd-assoc-main">
                  <div className="crmdd-assoc-nm">{contact.full_name}</div>
                  <div className="crmdd-assoc-sub">{contact.job_title || 'Contact'}</div>
                </div>
                {!lockContact && <button className="crmdd-assoc-x" onClick={() => setContactId(null)}>{'\u00D7'}</button>}
              </div>
            ) : (!lockContact && (
              <AssocPicker label={company ? 'Add a contact from ' + company.name : 'Add a contact'} items={contactPool} labelKey="full_name" onPick={c => setContactId(c.id)} />
            ))}
          </div>

          <div className="crmdd-more">
            <button className="crmdd-more-btn" onClick={() => setShowMore(!showMore)}>
              <span className="crmdd-dd-car">{showMore ? '\u25BE' : '\u25B8'}</span>
              <span>{showMore ? 'Fewer details' : 'More details'}</span>
            </button>
            {!showMore && <span className="crmdd-more-count">7 more fields</span>}
          </div>

          {showMore && (
            <div className="crmdd-more-panel">
              <div className="crmdd-grid2">
                <div><label className="crmdd-lbl">Priority</label>
                  <Dropdown value={priority} options={priOptions} onChange={setPriority} placeholder="Select" /></div>
                <div><label className="crmdd-lbl">Type</label>
                  <Dropdown value={oppType} options={typeOptions} onChange={setOppType} placeholder="Select" /></div>
              </div>
              <div className="crmdd-grid2">
                <div><label className="crmdd-lbl">Source</label>
                  <Dropdown value={source} options={srcOptions} onChange={setSource} placeholder="Select" /></div>
                <div><label className="crmdd-lbl">Owner</label>
                  <input className="crmdd-inp" value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="Deal owner" /></div>
              </div>
              <div className="crmdd-grid2">
                <div><label className="crmdd-lbl">Open date</label>
                  <input className="crmdd-inp" type="date" value={openDate} onChange={e => setOpenDate(e.target.value)} /></div>
                <div><label className="crmdd-lbl">Close date</label>
                  <input className="crmdd-inp" type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} /></div>
              </div>
              <div className="crmdd-field">
                <label className="crmdd-lbl">Description</label>
                <textarea className="crmdd-inp crmdd-ta" rows="2" value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this deal about?" />
              </div>
              <div className="crmdd-field">
                <label className="crmdd-lbl">Next step</label>
                <input className="crmdd-inp" value={nextStep} onChange={e => setNextStep(e.target.value)} placeholder="Send proposal by Friday" />
              </div>
              <div className="crmdd-field">
                <label className="crmdd-lbl">Internal notes</label>
                <textarea className="crmdd-inp crmdd-ta" rows="2" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything worth remembering" />
              </div>
            </div>
          )}

          {err && <div className="crmdd-err">{err}</div>}
        </div>

        <div className="crmdd-foot">
          <div className="crmdd-foot-l">
            <button className="crmdd-btn" onClick={() => save(false)} disabled={saving}>{saving ? 'Saving\u2026' : isEdit ? 'Save changes' : 'Create deal'}</button>
            {!isEdit && <button className="crmdd-btn-ghost" onClick={() => save(true)} disabled={saving}>Create and add another</button>}
            {isEdit && <button className="crmdd-btn-ghost" onClick={del}>Delete</button>}
          </div>
          <span className="crmdd-foot-hint">{'\u2318\u21B5'} to save</span>
        </div>

      </div>

      {confirm && (
        <div className="crmdd-confirm-ov" onMouseDown={e => { if (e.target === e.currentTarget) setConfirm(null); }}>
          <div className="crmdd-confirm">
            <h3 className="crmdd-confirm-t">{confirm.title}</h3>
            <p className="crmdd-confirm-b">{confirm.body}</p>
            <div className="crmdd-confirm-f">
              <button className="crmdd-btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className={confirm.danger ? 'crmdd-confirm-danger' : 'crmdd-btn'} onClick={() => { const fn = confirm.onConfirm; setConfirm(null); fn && fn(); }}>{confirm.confirmLabel || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
